import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import { 
  Package, AlertCircle, CheckCircle, XCircle, BarChart3, 
  RefreshCw, Box, Activity, Clock, User, TrendingUp, TrendingDown
} from 'lucide-react';

const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'];

export default function AdminPackingDashboard() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [stats, setStats] = useState({
    totalPacked: 0,
    packingNow: 0,
    completedToday: 0,
    avgPackingTime: 0,
    totalPackers: 0,
    activePackers: 0,
    shortages: 0,
    errors: 0
  });

  const [packingData, setPackingData] = useState([]);
  const [packerPerformance, setPackerPerformance] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [recentPacks, setRecentPacks] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('📊 Fetching dashboard data...');
      
      // Calculate date range
      const now = new Date();
      let startDate = new Date();
      
      switch(dateRange) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'yesterday':
          startDate.setDate(startDate.getDate() - 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setDate(startDate.getDate() - 30);
          break;
      }

      console.log('📅 Date range:', startDate.toISOString());

      // Fetch packing tasks WITHOUT relationships first
      const { data: packingTasks, error: packingError } = await supabase
        .from('packing_tasks')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      console.log('📦 Packing tasks fetched:', packingTasks?.length || 0);

      if (packingError) {
        console.error('❌ Error fetching tasks:', packingError);
        throw packingError;
      }

      const tasks = packingTasks || [];

      // Fetch user profiles separately for packer names
      const packerIds = [...new Set(tasks.map(t => t.packer_id).filter(Boolean))];
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, name, email')
        .in('id', packerIds);

      console.log('👥 Profiles fetched:', profiles?.length || 0);

      // Create a map of packer_id to profile
      const profileMap = {};
      profiles?.forEach(p => {
        profileMap[p.id] = p;
      });

      // Fetch order numbers separately
      const orderIds = [...new Set(tasks.map(t => t.order_id).filter(Boolean))];
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id, order_number, user_id, delivery_address_snapshot')
        .in('id', orderIds);

      console.log('📋 Orders fetched:', orders?.length || 0);

      // Create a map of order_id to order_number
      const orderMap = {};
      orders?.forEach(o => {
        orderMap[o.id] = o;
      });

      // Customers fetched separately — no orders→user_profiles relation in PostgREST
      const customerIds = [...new Set((orders || []).map(o => o.user_id).filter(Boolean))];
      const customerMap = {};
      if (customerIds.length) {
        const { data: custs } = await supabase
          .from('user_profiles')
          .select('id, name, phone')
          .in('id', customerIds);
        custs?.forEach(c => { customerMap[c.id] = c; });
      }

      // Enrich tasks with profile and order data
      const enrichedTasks = tasks.map(task => ({
        ...task,
        packer_name: profileMap[task.packer_id]?.name || 'Unknown',
        packer_email: profileMap[task.packer_id]?.email,
        order_number: orderMap[task.order_id]?.order_number || 'N/A',
        customer: customerMap[orderMap[task.order_id]?.user_id] || null,
        address: orderMap[task.order_id]?.delivery_address_snapshot || null
      }));

      // Calculate statistics
      const totalPacked = enrichedTasks.filter(t => t.status === 'completed').length;
      const packingNow = enrichedTasks.filter(t => t.status === 'in_progress').length;
      
      // Completed today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const completedToday = enrichedTasks.filter(t => 
        t.status === 'completed' && 
        t.completed_at &&
        new Date(t.completed_at) >= todayStart
      ).length;

      // Calculate average packing time (in minutes)
      const completedTasks = enrichedTasks.filter(t => 
        t.status === 'completed' && t.started_at && t.completed_at
      );
      
      const totalTime = completedTasks.reduce((sum, task) => {
        const start = new Date(task.started_at);
        const end = new Date(task.completed_at);
        return sum + ((end - start) / 1000 / 60); // minutes
      }, 0);
      
      const avgPackingTime = completedTasks.length > 0 
        ? (totalTime / completedTasks.length).toFixed(1)
        : 0;

      // Count unique packers
      const uniquePackers = new Set(
        enrichedTasks.filter(t => t.packer_id).map(t => t.packer_id)
      );
      
      const totalPackers = uniquePackers.size;

      // Active packers (packed in last hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const activePackers = new Set(
        enrichedTasks.filter(t => 
          t.packer_id && 
          t.completed_at && 
          new Date(t.completed_at) >= oneHourAgo
        ).map(t => t.packer_id)
      ).size;

      // Count shortages and errors
      const shortages = enrichedTasks.filter(t => t.has_shortages || t.has_discrepancies).length;
      const errors = enrichedTasks.filter(t => t.status === 'error').length;

      console.log('📊 Stats calculated:', {
        totalPacked,
        packingNow,
        completedToday,
        avgPackingTime,
        totalPackers,
        activePackers,
        shortages,
        errors
      });

      setStats({
        totalPacked,
        packingNow,
        completedToday,
        avgPackingTime,
        totalPackers,
        activePackers,
        shortages,
        errors
      });

      // Prepare packing trend data (last 7 days)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        
        const dayTasks = enrichedTasks.filter(t => {
          const taskDate = new Date(t.completed_at || t.created_at);
          return taskDate >= date && taskDate < nextDay && t.status === 'completed';
        });
        
        last7Days.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          packed: dayTasks.length,
          avgTime: dayTasks.length > 0
            ? (dayTasks.reduce((sum, t) => {
                if (!t.started_at || !t.completed_at) return sum;
                const time = (new Date(t.completed_at) - new Date(t.started_at)) / 1000 / 60;
                return sum + time;
              }, 0) / dayTasks.length).toFixed(1)
            : 0
        });
      }
      setPackingData(last7Days);

      // Packer performance (top 10)
      const packerStats = {};
      enrichedTasks.forEach(task => {
        if (!task.packer_id || task.status !== 'completed') return;
        
        if (!packerStats[task.packer_id]) {
          packerStats[task.packer_id] = {
            name: task.packer_name,
            packed: 0,
            totalTime: 0,
            count: 0
          };
        }
        
        packerStats[task.packer_id].packed++;
        
        if (task.started_at && task.completed_at) {
          const time = (new Date(task.completed_at) - new Date(task.started_at)) / 1000 / 60;
          packerStats[task.packer_id].totalTime += time;
          packerStats[task.packer_id].count++;
        }
      });

      const performance = Object.values(packerStats)
        .map(p => ({
          name: p.name,
          packed: p.packed,
          avgTime: p.count > 0 ? (p.totalTime / p.count).toFixed(1) : 0
        }))
        .sort((a, b) => b.packed - a.packed)
        .slice(0, 10);
      
      setPackerPerformance(performance);

      // Hourly distribution (today)
      const todayTasks = enrichedTasks.filter(t => {
        const taskDate = new Date(t.completed_at || t.created_at);
        return taskDate >= todayStart;
      });

      const hourly = Array.from({ length: 24 }, (_, hour) => {
        const hourTasks = todayTasks.filter(t => {
          const taskDate = new Date(t.completed_at || t.created_at);
          return taskDate.getHours() === hour;
        });
        
        return {
          hour: `${hour}:00`,
          packed: hourTasks.length
        };
      }).filter(h => h.packed > 0); // Only show hours with activity

      setHourlyData(hourly);

      // Recent packs (last 20)
      setRecentPacks(enrichedTasks.slice(0, 20));

      console.log('✅ Dashboard data loaded successfully');

    } catch (error) {
      console.error('💥 Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading packing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Box className="text-purple-600" size={36} />
              Packing Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Real-time packing operations monitoring</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Range Selector */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <button
              onClick={fetchDashboardData}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Packed */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Total Packed</p>
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.totalPacked}</p>
            <p className="text-sm text-gray-500 mt-1">Completed orders</p>
          </div>

          {/* Packing Now */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Packing Now</p>
              <Activity className="text-blue-600 animate-pulse" size={24} />
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.packingNow}</p>
            <p className="text-sm text-gray-500 mt-1">In progress</p>
          </div>

          {/* Avg Packing Time */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Avg Pack Time</p>
              <Clock className="text-purple-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.avgPackingTime}</p>
            <p className="text-sm text-gray-500 mt-1">Minutes per order</p>
          </div>

          {/* Active Packers */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Active Packers</p>
              <User className="text-indigo-600" size={24} />
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.activePackers}</p>
            <p className="text-sm text-gray-500 mt-1">Of {stats.totalPackers} total</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Packing Trend */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-purple-600" />
              Packing Trend (7 Days)
            </h3>
            {packingData.length > 0 && packingData.some(d => d.packed > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={packingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="packed" fill="#8b5cf6" name="Orders Packed" />
                  <Line yAxisId="right" type="monotone" dataKey="avgTime" stroke="#10b981" name="Avg Time (min)" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No data available for this period
              </div>
            )}
          </div>

          {/* Hourly Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-blue-600" />
              Hourly Distribution (Today)
            </h3>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="packed" fill="#3b82f6" name="Orders Packed" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No packing activity today yet
              </div>
            )}
          </div>
        </div>

        {/* Packer Performance */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User size={20} className="text-purple-600" />
            Top Packers Performance
          </h3>
          {packerPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Rank</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Packer</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Orders Packed</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Avg Time (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {packerPerformance.map((packer, idx) => (
                    <tr key={idx} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-500">#{idx + 1}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{packer.name}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                          {packer.packed}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700 font-medium">{packer.avgTime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No packer performance data available
            </div>
          )}
        </div>

        {/* Recent Packs */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package size={20} className="text-green-600" />
            Recent Packs
          </h3>
          {recentPacks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Order #</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Packed By</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPacks.map((pack) => (
                    <tr key={pack.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-mono text-sm font-medium text-purple-600">{pack.order_number}</td>
                      <td className="py-3 px-4 text-sm">
                        <p className="text-gray-900 font-medium">{pack.customer?.name || '—'}</p>
                        {pack.customer?.phone && <p className="text-gray-500 text-xs">{pack.customer.phone}</p>}
                        {pack.address && (
                          <p className="text-gray-500 text-xs">
                            {[pack.address.physical_address, pack.address.area].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">{pack.packer_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(pack.status)}`}>
                          {pack.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {pack.completed_at 
                          ? new Date(pack.completed_at).toLocaleTimeString()
                          : new Date(pack.created_at).toLocaleTimeString()
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No recent packing activity
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
